import { useTranslation } from "react-i18next";import React, { useState, useEffect, useRef } from 'react';
import {
  Route as RouteIcon, MapPin, Truck, Play, Check, Clock, Navigation,
  RefreshCw, Package, Plus, AlertTriangle, ChevronRight, ChevronDown,
  Settings, Target, Zap, BarChart3, ArrowRight, X, Loader2, Map,
  Send, Download, Maximize2 } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { toast } from 'sonner';
import { useFleetAPI, useVehicles } from '../hooks/useFleetData';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';


// Fix leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png'
});

// Custom icons
const depotIcon = new L.DivIcon({
  className: 'custom-marker',
  html: '<div style="background:#16a34a;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:14px;box-shadow:0 2px 6px rgba(0,0,0,0.3);">D</div>',
  iconSize: [32, 32],
  iconAnchor: [16, 16]
});

const deliveryIcon = (index) => new L.DivIcon({
  className: 'custom-marker',
  html: `<div style="background:#ef4444;color:white;width:28px;height:28px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-weight:bold;font-size:12px;box-shadow:0 2px 6px rgba(0,0,0,0.3);">${index + 1}</div>`,
  iconSize: [28, 28],
  iconAnchor: [14, 14]
});

// Route colors for multiple routes
const routeColors = ['#3b82f6', '#ef4444', '#16a34a', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const RouteOptimizationPage = () => {const { t } = useTranslation("fleet");
  const [activeTab, setActiveTab] = useState('optimize');
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optimizing, setOptimizing] = useState(false);
  const [showMapModal, setShowMapModal] = useState(false);

  // Form state
  const [depotLat, setDepotLat] = useState(-23.5505);
  const [depotLng, setDepotLng] = useState(-46.6333);
  const [depotAddress, setDepotAddress] = useState('São Paulo Distribution Center');
  const [deliveryPoints, setDeliveryPoints] = useState([
  { lat: -23.5629, lng: -46.6544, address: 'Av. Paulista, 1000', priority: 'NORMAL', timeWindow: '' },
  { lat: -23.5478, lng: -46.6358, address: 'Rua Augusta, 500', priority: 'HIGH', timeWindow: '' },
  { lat: -23.5734, lng: -46.6421, address: 'Av. Faria Lima, 2000', priority: 'NORMAL', timeWindow: '' }]
  );
  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const [maxStops, setMaxStops] = useState(10);
  const [optimizationStrategy, setOptimizationStrategy] = useState('distance');

  const { vehicles } = useVehicles({});
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const data = await fetchAPI('/route-optimization?limit=20');
      setJobs(data || []);
    } catch (err) {
      console.error('Failed to load jobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const runOptimization = async () => {
    if (selectedVehicles.length === 0) {
      toast.error('Please select at least one vehicle');
      return;
    }
    if (deliveryPoints.length === 0) {
      toast.error('Please add at least one delivery point');
      return;
    }

    setOptimizing(true);
    try {
      const result = await fetchAPI('/route-optimization', {
        method: 'POST',
        body: JSON.stringify({
          vehicle_ids: selectedVehicles,
          delivery_points: deliveryPoints,
          depot_lat: depotLat,
          depot_lng: depotLng,
          depot_address: depotAddress,
          max_stops_per_route: maxStops,
          optimization_strategy: optimizationStrategy
        })
      });

      setSelectedJob(result);
      toast.success('Optimization completed!');
      loadJobs();
      setActiveTab('results');
    } catch (err) {
      console.error('Optimization failed:', err);
      toast.error('Optimization failed: ' + (err.message || 'Unknown error'));
    } finally {
      setOptimizing(false);
    }
  };

  const dispatchRoutes = async (jobId) => {
    try {
      await fetchAPI(`/route-optimization/${jobId}/dispatch`, { method: 'POST' });
      toast.success('Routes dispatched to drivers!');
      loadJobs();
    } catch (err) {
      console.error('Dispatch failed:', err);
      toast.error('Failed to dispatch routes');
    }
  };

  const addDeliveryPoint = () => {
    const randomOffset = () => (Math.random() - 0.5) * 0.05;
    setDeliveryPoints([
    ...deliveryPoints,
    {
      lat: depotLat + randomOffset(),
      lng: depotLng + randomOffset(),
      address: `Delivery Point ${deliveryPoints.length + 1}`,
      priority: 'NORMAL',
      timeWindow: ''
    }]
    );
  };

  const removeDeliveryPoint = (index) => {
    setDeliveryPoints(deliveryPoints.filter((_, i) => i !== index));
  };

  const toggleVehicle = (vehicleId) => {
    if (selectedVehicles.includes(vehicleId)) {
      setSelectedVehicles(selectedVehicles.filter((id) => id !== vehicleId));
    } else {
      setSelectedVehicles([...selectedVehicles, vehicleId]);
    }
  };

  const statusColors = {
    PENDING: 'bg-yellow-100 text-yellow-700',
    PROCESSING: 'bg-blue-100 text-blue-700',
    COMPLETED: 'bg-green-100 text-green-700',
    FAILED: 'bg-red-100 text-red-700',
    DISPATCHED: 'bg-purple-100 text-purple-700'
  };

  const priorityColors = {
    LOW: 'bg-gray-100 text-gray-700',
    NORMAL: 'bg-blue-100 text-blue-700',
    HIGH: 'bg-amber-100 text-amber-700',
    URGENT: 'bg-red-100 text-red-700'
  };

  // Map bounds calculation
  const getMapBounds = () => {
    const points = [
    [depotLat, depotLng],
    ...deliveryPoints.map((p) => [p.lat, p.lng])];

    return points.length > 0 ? L.latLngBounds(points) : null;
  };

  const MapUpdater = () => {
    const map = useMap();
    useEffect(() => {
      const bounds = getMapBounds();
      if (bounds) {
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }, [deliveryPoints, depotLat, depotLng]);
    return null;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">{t("routeOptimization", "Route Optimization")}</h1>
          <p className="text-muted-foreground">{t("optimizeDeliveryRoutes", "Optimize delivery routes for maximum efficiency")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowMapModal(true)}>
            <Map className="w-4 h-4 mr-2" />{t("fullMap", "Full Map")}

          </Button>
          <Button onClick={loadJobs} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />{t("refresh", "Refresh")}

          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalJobs", "Total Jobs")}</p>
                <p className="text-2xl font-bold">{jobs.length}</p>
              </div>
              <RouteIcon className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("completed", "Completed")}</p>
                <p className="text-2xl font-bold text-green-600">
                  {jobs.filter((j) => j.status === 'COMPLETED').length}
                </p>
              </div>
              <Check className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalDistanceSaved", "Total Distance Saved")}</p>
                <p className="text-2xl font-bold text-purple-600">
                  {jobs.reduce((sum, j) => sum + (j.distance_saved_km || 0), 0).toFixed(1)}{t("km", "km")}
                </p>
              </div>
              <Target className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("avgEfficiency", "Avg. Efficiency")}</p>
                <p className="text-2xl font-bold text-amber-600">
                  {jobs.length > 0 ?
                  Math.round(jobs.reduce((sum, j) => sum + (j.optimization_score || 0), 0) / jobs.length) :
                  0}%
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="optimize">{t("newOptimization", "New Optimization")}</TabsTrigger>
          <TabsTrigger value="results">{t("results", "Results")}</TabsTrigger>
          <TabsTrigger value="history">{t("history", "History")}</TabsTrigger>
        </TabsList>

        {/* New Optimization Tab */}
        <TabsContent value="optimize" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Depot & Constraints */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-green-500" />{t("depotConstraints", "Depot & Constraints")}

                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{t("depotAddress", "Depot Address")}</Label>
                  <Input
                    value={depotAddress}
                    onChange={(e) => setDepotAddress(e.target.value)}
                    placeholder="Depot address" />
                  
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("latitude", "Latitude")}</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={depotLat}
                      onChange={(e) => setDepotLat(parseFloat(e.target.value) || 0)} />
                    
                  </div>
                  <div>
                    <Label>{t("longitude", "Longitude")}</Label>
                    <Input
                      type="number"
                      step="0.0001"
                      value={depotLng}
                      onChange={(e) => setDepotLng(parseFloat(e.target.value) || 0)} />
                    
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>{t("maxStopsPer", "Max Stops per Route")}</Label>
                    <Input
                      type="number"
                      value={maxStops}
                      onChange={(e) => setMaxStops(parseInt(e.target.value) || 10)} />
                    
                  </div>
                  <div>
                    <Label>{t("optimizationStrategy", "Optimization Strategy")}</Label>
                    <Select value={optimizationStrategy} onValueChange={setOptimizationStrategy}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="distance">{t("minimizeDistance", "Minimize Distance")}</SelectItem>
                        <SelectItem value="time">{t("minimizeTime", "Minimize Time")}</SelectItem>
                        <SelectItem value="balanced">{t("balanced", "Balanced")}</SelectItem>
                        <SelectItem value="priority">{t("priorityFirst", "Priority First")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Vehicle Selection */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Truck className="w-5 h-5 text-blue-500" />{t("selectVehicles", "Select Vehicles (")}
                  {selectedVehicles.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="max-h-64 overflow-auto space-y-2">
                  {vehicles.map((v) =>
                  <div
                    key={v.id}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${selectedVehicles.includes(v.id) ?
                    'bg-red-50 border-red-300 dark:bg-red-950/20' :
                    'hover:bg-muted border-border'}`
                    }
                    onClick={() => toggleVehicle(v.id)}>
                    
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center ${selectedVehicles.includes(v.id) ?
                        'bg-red-500 border-red-500' :
                        'border-muted-foreground'}`
                        }>
                            {selectedVehicles.includes(v.id) &&
                          <Check className="w-3 h-3 text-white" />
                          }
                          </div>
                          <div>
                            <p className="font-medium text-foreground">{v.vehicle_code}</p>
                            <p className="text-sm text-muted-foreground">{v.make} {v.model}</p>
                          </div>
                        </div>
                        <Badge className={v.status === 'AVAILABLE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {v.status}
                        </Badge>
                      </div>
                    </div>
                  )}
                  {vehicles.length === 0 &&
                  <p className="text-center text-muted-foreground py-4">{t("noVehiclesAvailable", "No vehicles available")}</p>
                  }
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Map Preview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Map className="w-5 h-5 text-purple-500" />{t("routePreview", "Route Preview")}

              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-[300px] rounded-lg overflow-hidden border">
                <MapContainer
                  center={[depotLat, depotLng]}
                  zoom={12}
                  style={{ height: '100%', width: '100%' }}>
                  
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  
                  <MapUpdater />

                  {/* Depot Marker */}
                  <Marker position={[depotLat, depotLng]} icon={depotIcon}>
                    <Popup>
                      <strong>{t("depot", "Depot")}</strong><br />
                      {depotAddress}
                    </Popup>
                  </Marker>

                  {/* Delivery Points */}
                  {deliveryPoints.map((point, index) =>
                  <Marker
                    key={index}
                    position={[point.lat, point.lng]}
                    icon={deliveryIcon(index)}>
                    
                      <Popup>
                        <strong>{t("stop", "Stop")}{index + 1}</strong><br />
                        {point.address}<br />{t("priority", "Priority:")}
                      {point.priority}
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Points */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-amber-500" />{t("deliveryPoints", "Delivery Points (")}
                  {deliveryPoints.length})
                </CardTitle>
                <Button size="sm" onClick={addDeliveryPoint}>
                  <Plus className="w-4 h-4 mr-1" />{t("addPoint", "Add Point")}

                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {deliveryPoints.map((point, index) =>
                <div key={index} className="flex items-center gap-4 p-3 bg-muted/30 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center text-red-600 font-medium">
                      {index + 1}
                    </div>
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-3">
                      <Input
                      placeholder="Address"
                      value={point.address}
                      onChange={(e) => {
                        const updated = [...deliveryPoints];
                        updated[index].address = e.target.value;
                        setDeliveryPoints(updated);
                      }}
                      className="md:col-span-2" />
                    
                      <Input
                      type="number"
                      step="0.0001"
                      placeholder="Lat"
                      value={point.lat}
                      onChange={(e) => {
                        const updated = [...deliveryPoints];
                        updated[index].lat = parseFloat(e.target.value) || 0;
                        setDeliveryPoints(updated);
                      }} />
                    
                      <Input
                      type="number"
                      step="0.0001"
                      placeholder="Lng"
                      value={point.lng}
                      onChange={(e) => {
                        const updated = [...deliveryPoints];
                        updated[index].lng = parseFloat(e.target.value) || 0;
                        setDeliveryPoints(updated);
                      }} />
                    
                      <Select
                      value={point.priority}
                      onValueChange={(value) => {
                        const updated = [...deliveryPoints];
                        updated[index].priority = value;
                        setDeliveryPoints(updated);
                      }}>
                      
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LOW">{t("low", "Low")}</SelectItem>
                          <SelectItem value="NORMAL">{t("normal", "Normal")}</SelectItem>
                          <SelectItem value="HIGH">{t("high", "High")}</SelectItem>
                          <SelectItem value="URGENT">{t("urgent", "Urgent")}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeDeliveryPoint(index)}>
                      <X className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Run Optimization */}
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => {
              setDeliveryPoints([]);
              setSelectedVehicles([]);
            }}>{t("clearAll", "Clear All")}

            </Button>
            <Button
              size="lg"
              className="bg-red-500 hover:bg-red-600"
              onClick={runOptimization}
              disabled={optimizing}>
              
              {optimizing ?
              <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />{t("optimizing", "Optimizing...")}

              </> :

              <>
                  <Zap className="w-5 h-5 mr-2" />{t("runOptimization", "Run Optimization")}

              </>
              }
            </Button>
          </div>
        </TabsContent>

        {/* Results Tab */}
        <TabsContent value="results" className="space-y-6">
          {selectedJob ?
          <>
              {/* Summary */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/20 border-green-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-green-600">{t("optimizationScore", "Optimization Score")}</p>
                    <p className="text-3xl font-bold text-green-700">{Math.round(selectedJob.optimization_score || 0)}%</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/20 border-blue-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-blue-600">{t("totalDistance", "Total Distance")}</p>
                    <p className="text-3xl font-bold text-blue-700">{Math.round(selectedJob.total_distance_km || 0)}{t("km", "km")}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/20 border-purple-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-purple-600">{t("distanceSaved", "Distance Saved")}</p>
                    <p className="text-3xl font-bold text-purple-700">{Math.round(selectedJob.distance_saved_km || 0)}{t("km", "km")}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-950/30 dark:to-amber-900/20 border-amber-200">
                  <CardContent className="p-4">
                    <p className="text-sm text-amber-600">{t("vehiclesUsed", "Vehicles Used")}</p>
                    <p className="text-3xl font-bold text-amber-700">{selectedJob.vehicles_used || 0}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Optimized Routes Map */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Map className="w-5 h-5 text-purple-500" />{t("optimizedRoutesMap", "Optimized Routes Map")}

                </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-[400px] rounded-lg overflow-hidden border">
                    <MapContainer
                    center={[selectedJob.depot_lat || depotLat, selectedJob.depot_lng || depotLng]}
                    zoom={12}
                    style={{ height: '100%', width: '100%' }}>
                    
                      <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    

                      {/* Depot Marker */}
                      <Marker
                      position={[selectedJob.depot_lat || depotLat, selectedJob.depot_lng || depotLng]}
                      icon={depotIcon}>
                      
                        <Popup><strong>{t("depot", "Depot")}</strong></Popup>
                      </Marker>

                      {/* Route Lines */}
                      {(selectedJob.optimized_routes || []).map((route, routeIndex) => {
                      const routePoints = [
                      [selectedJob.depot_lat || depotLat, selectedJob.depot_lng || depotLng],
                      ...(route.waypoints || []).map((wp) => [wp.lat, wp.lng]),
                      [selectedJob.depot_lat || depotLat, selectedJob.depot_lng || depotLng]];

                      return (
                        <React.Fragment key={routeIndex}>
                            <Polyline
                            positions={routePoints}
                            color={routeColors[routeIndex % routeColors.length]}
                            weight={4}
                            opacity={0.8} />
                          
                            {(route.waypoints || []).map((wp, wpIndex) =>
                          <Marker
                            key={`${routeIndex}-${wpIndex}`}
                            position={[wp.lat, wp.lng]}
                            icon={deliveryIcon(wpIndex)}>
                            
                                <Popup>
                                  <strong>{t("route", "Route")}{routeIndex + 1}{t("stop", "- Stop")}{wpIndex + 1}</strong><br />
                                  {wp.address || `${(wp.lat ?? 0).toFixed(4)}, ${(wp.lng ?? 0).toFixed(4)}`}
                                </Popup>
                              </Marker>
                          )}
                          </React.Fragment>);

                    })}
                    </MapContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Routes List */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t("optimizedRoutes", "Optimized Routes")}</CardTitle>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => {}}>
                        <Download className="w-4 h-4 mr-2" />{t("export", "Export")}

                    </Button>
                      <Button
                      onClick={() => dispatchRoutes(selectedJob.id)}
                      className="bg-green-500 hover:bg-green-600"
                      disabled={selectedJob.status === 'DISPATCHED'}>
                      
                        <Send className="w-4 h-4 mr-2" />
                        {selectedJob.status === 'DISPATCHED' ? 'Dispatched' : 'Dispatch All'}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(selectedJob.optimized_routes || []).map((route, index) =>
                  <div key={index} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div
                          className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                          style={{ background: routeColors[index % routeColors.length] }}>
                          
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium">{t("route", "Route")}{index + 1}</p>
                              <p className="text-sm text-muted-foreground">{route.total_stops || route.waypoints?.length || 0}{t("stops", "stops")}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">{Math.round(route.total_distance_km || 0)}{t("km", "km")}</p>
                            <p className="text-sm text-muted-foreground">{Math.round(route.total_duration_minutes || 0)}{t("min", "min")}</p>
                          </div>
                        </div>

                        {/* Route waypoints */}
                        <div className="flex items-center gap-2 overflow-x-auto pb-2">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 text-white text-xs font-medium flex-shrink-0">
                            D
                          </div>
                          {(route.waypoints || []).map((wp, wpIndex) =>
                      <React.Fragment key={wpIndex}>
                              <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                              <div
                          className="flex items-center justify-center w-8 h-8 rounded-full text-white text-xs font-medium flex-shrink-0"
                          style={{ background: routeColors[index % routeColors.length] }}>
                          
                                {wpIndex + 1}
                              </div>
                            </React.Fragment>
                      )}
                          <ArrowRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500 text-white text-xs font-medium flex-shrink-0">
                            D
                          </div>
                        </div>
                      </div>
                  )}

                    {(!selectedJob.optimized_routes || selectedJob.optimized_routes.length === 0) &&
                  <div className="text-center py-8 text-muted-foreground">{t("noRoutesGenerated", "No routes generated yet")}

                  </div>
                  }
                  </div>
                </CardContent>
              </Card>
            </> :

          <Card>
              <CardContent className="p-12 text-center">
                <RouteIcon className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                <h3 className="text-lg font-medium text-foreground mb-2">{t("noOptimizationResults", "No Optimization Results")}</h3>
                <p className="text-muted-foreground mb-4">{t("runAnOptimization", "Run an optimization to see results here")}</p>
                <Button onClick={() => setActiveTab('optimize')}>{t("createNewOptimization", "Create New Optimization")}

              </Button>
              </CardContent>
            </Card>
          }
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>{t("optimizationHistory", "Optimization History")}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ?
              <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div> :
              jobs.length === 0 ?
              <div className="text-center py-8 text-muted-foreground">{t("noOptimizationJobs", "No optimization jobs yet")}

              </div> :

              <div className="space-y-3">
                  {jobs.map((job) =>
                <div
                  key={job.id}
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/30 cursor-pointer transition-colors"
                  onClick={() => {
                    setSelectedJob(job);
                    setActiveTab('results');
                  }}>
                  
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                          <RouteIcon className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="font-medium">{job.job_code}</p>
                          <p className="text-sm text-muted-foreground">
                            {job.deliveries_count}{t("deliveries", "deliveries \u2192")}{job.vehicles_used || 0}{t("vehicles", "vehicles")}
                      </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-medium">{Math.round(job.total_distance_km || 0)}{t("km", "km")}</p>
                          <p className="text-sm text-green-600">-{Math.round(job.distance_saved_km || 0)}{t("kmSaved", "km saved")}</p>
                        </div>
                        <Badge className={statusColors[job.status] || 'bg-gray-100'}>
                          {job.status}
                        </Badge>
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      </div>
                    </div>
                )}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Full Map Modal */}
      <Dialog open={showMapModal} onOpenChange={setShowMapModal}>
        <DialogContent className="max-w-4xl h-[80vh]">
          <DialogHeader>
            <DialogTitle>{t("routeMap", "Route Map")}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 h-full min-h-[500px]">
            <MapContainer
              center={[depotLat, depotLng]}
              zoom={12}
              style={{ height: '100%', width: '100%', minHeight: '500px' }}>
              
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              

              {/* Depot */}
              <Marker position={[depotLat, depotLng]} icon={depotIcon}>
                <Popup><strong>{t("depot", "Depot")}</strong><br />{depotAddress}</Popup>
              </Marker>

              {/* Delivery Points */}
              {deliveryPoints.map((point, index) =>
              <Marker
                key={index}
                position={[point.lat, point.lng]}
                icon={deliveryIcon(index)}>
                
                  <Popup>
                    <strong>{t("stop", "Stop")}{index + 1}</strong><br />
                    {point.address}<br />{t("priority", "Priority:")}
                  {point.priority}
                  </Popup>
                </Marker>
              )}
            </MapContainer>
          </div>
        </DialogContent>
      </Dialog>
    </div>);

};

export default RouteOptimizationPage;